import {useEffect} from 'react'
import {TRACKING_LOADING_DELAY} from '../../constants'
import {useInteractionStudioLoader} from '../../interaction-studio-api'

/**
 * a hook to load interaction studio pixel
 * @param {boolean} loadImmediately - if true, load the pixel immediately, otherwise wait for the TRACKING_LOADING_DELAY
 */
const useInteractionStudioPixel = ({loadImmediately} = {}) => {
    const loadIS = useInteractionStudioLoader()

    // activate loading immediately or after a delay
    useEffect(() => {
        if (loadImmediately) {
            loadIS()
            return
        }

        const timer = setTimeout(() => {
            loadIS()
        }, TRACKING_LOADING_DELAY)
        return () => clearTimeout(timer)
    }, [loadImmediately, loadIS])
}

export default useInteractionStudioPixel

/**
 * @typedef { "AddToCart" | "ClickThrough" | "Comment" | "Favorite" | "Purchase" | "QuickViewItem" | "RemoveFromCart" | "Review" | "Search" | "SearchClick" | "SearchViewResults" | "Share" | "StopQuickViewItem" | "UpdateLineItem" | "ViewBanditItems" | "ViewCart" | "ViewCategory" | "ViewItem" | "ViewItemDetail" | "ViewItemOutOfStock" | "ViewTag" } ItemAction
 *
 */

/**
 * Sending tracking event to interaction studio
 * @param {string} action name of the specific action
 * @param {ItemAction} itemAction predefined item action
 * @param {Object} payloadData data payload
 * @returns
 */
export const trackInteractionStudioEvent = (action, itemAction, payloadData) => {
    if (!window) return
    const evergage = window.Evergage
    if (!evergage) return

    evergage.sendEvent({
        action,
        itemAction: evergage.ItemAction[itemAction],
        ...payloadData
    })
}

/**
 * Track add to wishlist for Interaction studio
 * @param {Object} product product added to wishlist
 * @param {ItemAction} itemAction
 */
export const trackInteractionAddToWishlist = (product, itemAction) => {
    if (!product) return
    const salesforceInteractions = window.SalesforceInteractions

    const payload = {
        user: {
            id: salesforceInteractions ? salesforceInteractions.getAnonymousId() : undefined
        },
        item: {
            id: product.productId,
            itemType: 'product'
        }
    }

    trackInteractionStudioEvent('Add to Wishlist', itemAction, payload)
}

/**
 * Track add to cart for Interaction studio
 * @param {Object} product product added to cart
 */
export const trackInteractionBundleAddedToCart = (product) => {
    if (!product) return

    const evergage = window.Evergage
    if (!evergage) return

    evergage.sendEvent({
        itemAction: 'AddToCart',
        cart: {
            singleLine: {
                product: product
            }
        }
    })
}
